import { Button } from "@/components/Button/Button";
import { CloseSvg } from "@/components/Icon/Icon";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Link, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

const COOKIE_CONSENT_KEY = "cookie-consent";
type CookieConsent = "all" | "essential" | "reject";
const COOKIE_CONSENT_OPTIONS: CookieConsent[] = ["all", "essential", "reject"];

const getCookieConsentState = (): CookieConsent | undefined => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY) ?? undefined;

  return consent !== undefined && COOKIE_CONSENT_OPTIONS.includes(consent as CookieConsent) ? (consent as CookieConsent) : undefined;
};

const setCookieConsentState = (consent: CookieConsent) => {
  localStorage.setItem(COOKIE_CONSENT_KEY, consent);
};

export const CookieConsentDialog = () => {
  const { t } = useTranslation();
  const { logout } = useAuth0();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const consent = getCookieConsentState();
    if (consent === undefined || consent === "reject") {
      setIsOpen(true);
    }
  }, []);

  const handleClose = (choice?: CookieConsent) => {
    setCookieConsentState(choice ?? "all");
    setIsOpen(false);

    if (choice === "reject") {
      logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleClose()}
      sx={{
        ".MuiPaper-root": {
          width: "100%",
          maxWidth: "600px"
        }
      }}
    >
      <Box component="div" sx={{ display: "flex", width: "100%", alignItems: "center" }}>
        <DialogTitle>{t("title.cookiePolicy")}</DialogTitle>
        <Box
          component="div"
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            paddingRight: "16px"
          }}
        >
          <IconButton onClick={() => handleClose()} sx={{ height: "40px" }}>
            <CloseSvg color="inherit" />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <DialogContent>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          <Trans
            t={t}
            components={{
              cookie: (
                <Link
                  component={"button"}
                  onClick={() => {
                    window.open("https://www.asiga.com/privacy-policy/#cookie-policy", "_blank", "noopener");
                  }}
                />
              ),
              privacy: (
                <Link
                  component={"button"}
                  onClick={() => {
                    window.open("https://www.asiga.com/privacy-policy/", "_blank", "noopener");
                  }}
                />
              )
            }}
            i18nKey={"message.cookiePolicy"}
          />
        </Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ padding: "1em", gap: "0.5em", justifyContent: "center", flexDirection: ["column", "row"] }}>
        <Button
          sx={{ flexGrow: 1, flexShrink: 0, flexBasis: 1, minWidth: ["100%", "fit-content"] }}
          variant="outlined"
          onClick={() => handleClose("reject")}
        >
          {t("button.rejectAll")}
        </Button>
        <Button
          sx={{ flexGrow: 1, flexShrink: 0, flexBasis: 1, minWidth: ["100%", "fit-content"] }}
          variant="outlined"
          onClick={() => handleClose("essential")}
        >
          {t("button.essentialOnly")}
        </Button>
        <Button
          sx={{ flexGrow: 1, flexShrink: 0, flexBasis: 1, minWidth: ["100%", "fit-content"] }}
          color={"primary"}
          onClick={() => handleClose("all")}
        >
          {t("button.acceptAll")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
